import type { TimelineSample } from '../types/market'
import { clamp } from '../utils/math'

export type TimelineListener = (sample: TimelineSample) => void

export class TimelineEngine {
  private currentMs = 0
  private playing = false
  private speed = 1
  private lastTimestamp = 0
  private readonly listeners = new Set<TimelineListener>()

  constructor(
    private readonly frameCount: number,
    private readonly marketMinuteDuration: number,
  ) {}

  get durationMs(): number {
    return Math.max(0, this.frameCount - 1) * this.marketMinuteDuration
  }

  get elapsedMs(): number {
    return this.currentMs
  }

  get isPlaying(): boolean {
    return this.playing
  }

  get playbackSpeed(): number {
    return this.speed
  }

  subscribe(listener: TimelineListener): () => void {
    this.listeners.add(listener)
    listener(this.sample())
    return () => this.listeners.delete(listener)
  }

  play(): void {
    if (this.playing || this.currentMs >= this.durationMs) return
    this.playing = true
    this.lastTimestamp = performance.now()
  }

  pause(): void {
    this.playing = false
  }

  restart(): void {
    this.seekMs(0)
    this.play()
  }

  setSpeed(speed: number): void {
    this.speed = clamp(speed, 0.1, 8)
  }

  seekMs(milliseconds: number): void {
    this.currentMs = clamp(milliseconds, 0, this.durationMs)
    this.emit()
  }

  seekFrame(index: number): void {
    this.seekMs(clamp(index, 0, this.frameCount - 1) * this.marketMinuteDuration)
  }

  nextFrame(): void {
    this.seekFrame(Math.floor(this.currentMs / this.marketMinuteDuration) + 1)
  }

  previousFrame(): void {
    const exact = this.currentMs / this.marketMinuteDuration
    this.seekFrame(Math.ceil(exact) - 1)
  }

  sampleAt(milliseconds: number): TimelineSample {
    const elapsedMs = clamp(milliseconds, 0, this.durationMs)
    const position = elapsedMs / this.marketMinuteDuration
    const leftIndex = Math.min(Math.floor(position), this.frameCount - 1)
    const rightIndex = Math.min(leftIndex + 1, this.frameCount - 1)
    return {
      leftIndex,
      rightIndex,
      progress: rightIndex === leftIndex ? 0 : position - leftIndex,
      elapsedMs,
      durationMs: this.durationMs,
    }
  }

  sample(): TimelineSample {
    return this.sampleAt(this.currentMs)
  }

  advance(timestamp: number): void {
    if (!this.playing) return
    const delta = Math.min(64, Math.max(0, timestamp - this.lastTimestamp)) * this.speed
    this.lastTimestamp = timestamp
    this.currentMs = Math.min(this.durationMs, this.currentMs + delta)
    this.emit()
    if (this.currentMs >= this.durationMs) this.playing = false
  }

  destroy(): void {
    this.pause()
    this.listeners.clear()
  }

  private emit(): void {
    const sample = this.sample()
    this.listeners.forEach((listener) => listener(sample))
  }
}
